import type { AdminAuditEntry, AdminCallListItem } from '@/shared/api/types'

const number = new Intl.NumberFormat('ru-RU')
const decimal = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 })
const money = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

export function formatNumber(value: number): string {
  return number.format(value)
}

export function formatDecimal(value: number): string {
  return decimal.format(value)
}

export function formatUsd(value: number): string {
  return money.format(value)
}

export function formatPercent(value: number): string {
  return `${decimal.format(value * 100)}%`
}

export function formatAdminDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatDay(value: string): string {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
}

export function formatDurationMs(value: number): string {
  return formatDurationSeconds(Math.round(value / 1000))
}

export function formatDurationSeconds(value: number): string {
  if (value <= 0) return '0 с'
  const hours = Math.floor(value / 3600)
  const minutes = Math.floor((value % 3600) / 60)
  const seconds = value % 60
  if (hours > 0) return `${hours} ч ${minutes} мин`
  if (minutes > 0) return `${minutes} мин ${seconds} с`
  return `${seconds} с`
}

export function callKindLabel(kind: AdminCallListItem['kind'] | string): string {
  if (kind === 'test') return 'Тестовый'
  if (kind === 'phone' || kind === 'real') return 'Телефонный'
  return 'Другой'
}

export function callStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Ожидает',
    active: 'Идёт',
    completed: 'Завершён',
    failed: 'Ошибка',
  }
  return labels[status] ?? 'Неизвестно'
}

export function callStatusTone(status: string): 'good' | 'danger' | 'warn' | 'neutral' {
  if (status === 'completed') return 'good'
  if (status === 'failed') return 'danger'
  if (status === 'active' || status === 'pending') return 'warn'
  return 'neutral'
}

export function campaignStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    running: 'Идёт',
    paused: 'Приостановлен',
    stopped: 'Остановлен',
    done: 'Завершён',
    completed: 'Завершён',
  }
  return labels[status] ?? 'Неизвестно'
}

const ACTION_LABELS: Record<string, string> = {
  'company.rename': 'Переименовал компанию',
  company_rename: 'Переименовал компанию',
  rename_company: 'Переименовал компанию',
  'company.block': 'Заблокировал компанию',
  company_block: 'Заблокировал компанию',
  block_company: 'Заблокировал компанию',
  'company.unblock': 'Разблокировал компанию',
  company_unblock: 'Разблокировал компанию',
  unblock_company: 'Разблокировал компанию',
  'company.voice.update': 'Изменил настройки голоса',
  'voice.update': 'Изменил настройки голоса',
  company_voice_update: 'Изменил настройки голоса',
  update_voice: 'Изменил настройки голоса',
  'user.reset_password': 'Сбросил пароль пользователя',
  user_reset_password: 'Сбросил пароль пользователя',
  reset_password: 'Сбросил пароль пользователя',
  'campaign.pause': 'Приостановил обзвон',
  campaign_pause: 'Приостановил обзвон',
  pause_campaign: 'Приостановил обзвон',
  'campaign.stop': 'Остановил обзвон',
  campaign_stop: 'Остановил обзвон',
  stop_campaign: 'Остановил обзвон',
  'impersonation.start': 'Вошёл в кабинет клиента',
  impersonation_start: 'Вошёл в кабинет клиента',
  impersonate: 'Вошёл в кабинет клиента',
  'impersonation.mutation': 'Выполнил действие в кабинете клиента',
  'impersonation.stop': 'Вернулся в админку',
  impersonation_stop: 'Вернулся в админку',
  stop_impersonation: 'Вернулся в админку',
}

export function auditActionLabel(action: string): string {
  const known = ACTION_LABELS[action]
  if (known) return known

  const normalized = action.toLowerCase().replace(/[.-]/g, '_')
  if (normalized.includes('unblock')) return 'Разблокировал компанию'
  if (normalized.includes('block')) return 'Заблокировал компанию'
  if (normalized.includes('rename')) return 'Переименовал компанию'
  if (normalized.includes('voice')) return 'Изменил настройки голоса'
  if (normalized.includes('password') && normalized.includes('reset')) {
    return 'Сбросил пароль пользователя'
  }
  if (normalized.includes('campaign') && normalized.includes('pause')) {
    return 'Приостановил обзвон'
  }
  if (normalized.includes('campaign') && normalized.includes('stop')) {
    return 'Остановил обзвон'
  }
  if (normalized.includes('imperson') && normalized.includes('stop')) return 'Вернулся в админку'
  if (normalized.includes('imperson')) return 'Вошёл в кабинет клиента'
  return 'Выполнил административное действие'
}

const DETAIL_LABELS: Record<string, string> = {
  oldName: 'Прежнее название',
  newName: 'Новое название',
  name: 'Название',
  companyId: 'Компания',
  campaignId: 'Обзвон',
  userId: 'Пользователь',
  ttsModel: 'Модель синтеза',
  llmModel: 'Диалоговая модель',
  voiceId: 'Голос',
  speed: 'Темп',
}

export function auditDetailsText(details: AdminAuditEntry['details']): string {
  if (!details || Object.keys(details).length === 0) return ''
  return Object.entries(details)
    .map(([key, value]) => `${DETAIL_LABELS[key] ?? 'Деталь'}: ${formatDetailValue(value)}`)
    .join(' · ')
}

function formatDetailValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return JSON.stringify(value)
}
